import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { 
  LoginRequest, 
  LoginResponse, 
  DashboardStatsResponse,
  AdminUser,
  SystemSettings,
  AdminSettings,
  CreateAdminRequest,
  UpdateAdminRequest,
  SystemSettingsRequest,
  AdminSettingsRequest
} from '../../types/admin';
import { 
  User, 
  Product, 
  Report, 
  SupportInquiry,
  Transaction,
  GetUsersRequest,
  GetProductsRequest,
  PaginatedResponse 
} from '../../types/app';
import { RootState } from '../store';
import { logout } from '../slices/authSlice';

// 環境に応じたAPIのbaseURLを設定
const getApiBaseUrl = () => {
  const environment = process.env.REACT_APP_FIREBASE_ENV || 'test';
  
  switch (environment) {
    case 'production':
      return 'https://adminapi-a2b5o5ydga-uc.a.run.app';
    case 'test':
      return 'https://adminapi-xjh3gncbvq-uc.a.run.app';
    default:
      return 'https://adminapi-xjh3gncbvq-uc.a.run.app';
  }
};

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    
    // デバッグ用：現在のAPIのbaseURLをログ出力
    console.log(`🌐 API baseURL: ${getApiBaseUrl()}`);
    
    return headers;
  },
});

const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // パラメータのデバッグログ
  console.log('BaseQuery args:', args);
  if (typeof args === 'object' && 'params' in args) {
    console.log('BaseQuery params:', args.params);
  }
  
  const result = await baseQuery(args, api, extraOptions);
  
  // 401エラーの場合は自動ログアウト
  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    // ログインページにリダイレクト
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }
  
  return result;
};

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['User', 'Product', 'Report', 'Inquiry', 'Dashboard', 'Admin', 'Transaction', 'Chat', 'BankTransfer', 'Settings', 'Notification', 'PayoutRequest', 'Announcement'],
  endpoints: (builder) => ({
    // 認証関連
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
    }),
    
    verifyToken: builder.query<AdminUser, void>({
      query: () => '/auth/verify-token',
      providesTags: ['Admin'],
    }),

    // ダッシュボード関連
    getDashboardStats: builder.query<DashboardStatsResponse, void>({
      query: () => '/dashboard/stats',
      providesTags: ['Dashboard'],
    }),

    // ユーザー管理関連
    getUsers: builder.query<PaginatedResponse<User>, GetUsersRequest>({
      query: (params) => ({
        url: '/users',
        params,
      }),
      providesTags: ['User'],
    }),

    getUser: builder.query<User, string>({
      query: (id) => `/users/${id}`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),

    verifyUser: builder.mutation<User, { id: string; approved: boolean; reason?: string }>({
      query: ({ id, ...body }) => ({
        url: `/users/${id}/verify`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        'User',
        'Dashboard'
      ],
    }),

    banUser: builder.mutation<User, { id: string; banned: boolean; reason?: string }>({
      query: ({ id, ...body }) => ({
        url: `/users/${id}/ban`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        'User',
        'Dashboard'
      ],
    }),

    blockUserPurchase: builder.mutation<User, { id: string; blocked: boolean; reason?: string }>({
      query: ({ id, ...body }) => ({
        url: `/users/${id}/block-purchase`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        'User',
        'Dashboard'
      ],
    }),

    deleteUser: builder.mutation<void, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/users/${id}`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        'User',
        'Dashboard'
      ],
    }),

    restoreUser: builder.mutation<void, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/users/${id}/restore`,
        method: 'PUT',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'User', id },
        'User',
        'Dashboard'
      ],
    }),

    // 商品管理関連
    getProducts: builder.query<PaginatedResponse<Product>, GetProductsRequest>({
      query: (params) => ({
        url: '/products',
        params,
      }),
      transformResponse: (response: any): PaginatedResponse<Product> => {
        const items = response.products || [];
        const totalCount = response.total || 0;
        const page = response.page || 1;
        const limit = response.limit || 20;
        return {
          items,
          totalCount,
          page,
          limit,
          totalPages: Math.ceil(totalCount / limit),
        };
      },
      providesTags: ['Product'],
    }),

    getProduct: builder.query<Product, string>({
      query: (id) => `/products/${id}`,
      providesTags: (result, error, id) => [{ type: 'Product', id }],
    }),

    deleteProduct: builder.mutation<void, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/products/${id}`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        'Product',
        'Dashboard'
      ],
    }),

    hideProduct: builder.mutation<Product, { id: string; hidden: boolean; reason?: string }>({
      query: ({ id, ...body }) => ({
        url: `/products/${id}/hide`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        'Product'
      ],
    }),

    reviewProduct: builder.mutation<Product, { id: string; approved: boolean; notes?: string }>({
      query: ({ id, ...body }) => ({
        url: `/products/${id}/review`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        'Product',
        'Dashboard'
      ],
    }),

    // 特集商品管理関連
    getFeaturedProducts: builder.query<any, void>({
      query: () => '/featured-products',
      providesTags: ['Product'],
    }),

    updateFeaturedProducts: builder.mutation<any, { type: string; productIds: string[] }>({
      query: ({ type, productIds }) => ({
        url: `/featured-products/${type}`,
        method: 'PUT',
        body: { productIds },
      }),
      invalidatesTags: ['Product'],
    }),

    // 商品コメント管理関連
    getProductComments: builder.query<PaginatedResponse<any>, { page?: number; limit?: number }>({
      query: (params) => ({
        url: '/product-comments',
        params,
      }),
      providesTags: ['Product'],
    }),

    getProductCommentThreads: builder.query<{ threads: any[] }, string>({
      query: (productId) => `/product-comments/${productId}/threads`,
    }),

    // 通報管理関連
    getReports: builder.query<PaginatedResponse<Report>, GetUsersRequest>({
      query: (params) => ({
        url: '/reports',
        params,
      }),
      providesTags: ['Report'],
    }),

    getReport: builder.query<Report, string>({
      query: (id) => `/reports/${id}`,
      providesTags: (result, error, id) => [{ type: 'Report', id }],
    }),

    resolveReport: builder.mutation<Report, { 
      id: string; 
      action: 'APPROVED' | 'REJECTED'; 
      reason: string;
      banUser?: boolean; // ユーザー通報時にBANするかどうか
    }>({
      query: ({ id, ...body }) => ({
        url: `/reports/${id}/resolve`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Report', id },
        'Report',
        'Dashboard',
        'User', // ユーザーBANの場合に更新
        'Product' // 商品削除の場合に更新
      ],
    }),

    // 問い合わせ管理関連
    getInquiries: builder.query<PaginatedResponse<SupportInquiry>, GetUsersRequest>({
      query: (params) => ({
        url: '/inquiries',
        params,
      }),
      providesTags: ['Inquiry'],
    }),

    getInquiry: builder.query<SupportInquiry, string>({
      query: (id) => `/inquiries/${id}`,
      providesTags: (result, error, id) => [{ type: 'Inquiry', id }],
    }),

    respondToInquiry: builder.mutation<SupportInquiry, { 
      id: string; 
      response: string;
      status?: 'resolved' | 'closed' 
    }>({
      query: ({ id, ...body }) => ({
        url: `/inquiries/${id}/respond`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Inquiry', id },
        'Inquiry',
        'Dashboard'
      ],
    }),

    // 取引管理関連
    getTransactions: builder.query<PaginatedResponse<Transaction>, GetUsersRequest>({
      query: (params) => ({
        url: '/transactions',
        params,
      }),
      providesTags: ['Transaction'],
    }),

    getTransaction: builder.query<Transaction, string>({
      query: (id) => `/transactions/${id}`,
      providesTags: (result, error, id) => [{ type: 'Transaction', id }],
    }),

    confirmPayment: builder.mutation<Transaction, { id: string; confirmed: boolean }>({
      query: ({ id, ...body }) => ({
        url: `/transactions/${id}/confirm-payment`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Transaction', id },
        'Transaction',
        'Dashboard'
      ],
    }),

    excludeFromStats: builder.mutation<{ success: boolean; message: string; transactionId: string; type: string; excludeFromStats: boolean }, { type: 'chat' | 'transaction' | 'payout_request'; id: string; exclude: boolean }>({
      query: ({ type, id, exclude }) => ({
        url: `/transactions/${type}/${id}/exclude-from-stats`,
        method: 'PUT',
        body: { exclude },
      }),
      invalidatesTags: (result, error, { type, id }) => [
        { type: type === 'chat' ? 'Chat' : type === 'payout_request' ? 'PayoutRequest' : 'Transaction', id },
        'Transaction',
        'Chat',
        'PayoutRequest',
        'Dashboard'
      ],
    }),

    // チャット管理関連
    getChats: builder.query<any, { page?: number; limit?: number; showExcluded?: string }>({
      query: (params) => ({
        url: '/chats',
        params,
      }),
      providesTags: ['Chat'],
    }),

    getChat: builder.query<any, string>({
      query: (id) => `/chats/${id}`,
      providesTags: (result, error, id) => [{ type: 'Chat', id }],
    }),

    getChatMessages: builder.query<any, { chatId: string; limit?: number }>({
      query: ({ chatId, limit = 50 }) => ({
        url: `/chats/${chatId}/messages`,
        params: { limit },
      }),
      providesTags: ['Chat'],
    }),
    
    sendAdminContact: builder.mutation<any, { 
      chatId: string; 
      recipientType: 'buyer' | 'seller' | 'both'; 
      subject: string; 
      message: string; 
    }>({
      query: ({ chatId, ...body }) => ({
        url: `/chats/${chatId}/admin-contact`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Chat'],
    }),

    // 銀行振り込み管理関連
    getBankTransfers: builder.query<any, { status?: string; page?: number; limit?: number; showExcluded?: string }>({
      query: (params) => ({
        url: '/bank-transfers',
        params,
      }),
      providesTags: ['BankTransfer'],
    }),

    getBankTransferStats: builder.query<{ pending: number; completed: number; total: number }, { showExcluded?: string } | void>({
      query: (params) => ({
        url: '/bank-transfers/stats',
        params: params || {},
      }),
      providesTags: ['BankTransfer'],
    }),

    getOtherPaymentStats: builder.query<{ pending: number; completed: number; total: number }, { showExcluded?: string } | void>({
      query: (params) => ({
        url: '/transactions/other-payments/stats',
        params: params || {},
      }),
      providesTags: ['Transaction'],
    }),

    confirmBankTransfer: builder.mutation<any, { chatId: string; notes?: string }>({
      query: ({ chatId, ...body }) => ({
        url: `/bank-transfers/${chatId}/confirm`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['BankTransfer', 'Chat', 'Transaction'],
    }),

    // 設定管理関連
    getSystemSettings: builder.query<SystemSettings, void>({
      query: () => '/settings/system',
      providesTags: ['Settings'],
    }),

    updateSystemSettings: builder.mutation<SystemSettings, SystemSettingsRequest>({
      query: (body) => ({
        url: '/settings/system',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Settings'],
    }),

    getAdminSettings: builder.query<AdminSettings, void>({
      query: () => '/settings/admin',
      providesTags: ['Settings'],
    }),

    updateAdminSettings: builder.mutation<AdminSettings, AdminSettingsRequest>({
      query: (body) => ({
        url: '/settings/admin',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Settings'],
    }),

    getAdministrators: builder.query<AdminUser[], void>({
      query: () => '/settings/administrators',
      providesTags: ['Admin'],
    }),

    createAdministrator: builder.mutation<AdminUser, CreateAdminRequest>({
      query: (body) => ({
        url: '/settings/administrators',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Admin'],
    }),

    updateAdministrator: builder.mutation<AdminUser, { id: string; data: UpdateAdminRequest }>({
      query: ({ id, data }) => ({
        url: `/settings/administrators/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Admin'],
    }),

    deleteAdministrator: builder.mutation<void, string>({
      query: (id) => ({
        url: `/settings/administrators/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Admin'],
    }),

    // プッシュ通知管理関連
    getScheduledNotifications: builder.query<any, { status?: string; page?: number; limit?: number }>({
      query: (params) => ({
        url: '/notifications',
        params,
      }),
      providesTags: ['Notification'],
    }),

    sendNotification: builder.mutation<any, {
      title: string;
      body: string;
      targetType: 'all_devices' | 'all' | 'logged_in' | 'verified' | 'individual';
      targetUserIds?: string[];
      scheduledAt?: string;
    }>({
      query: (body) => ({
        url: '/notifications/send',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Notification'],
    }),

    cancelNotification: builder.mutation<any, string>({
      query: (id) => ({
        url: `/notifications/${id}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: ['Notification'],
    }),

    getNotificationSettings: builder.query<any, void>({
      query: () => '/notifications/settings',
      providesTags: ['Notification'],
    }),

    updateNotificationSettings: builder.mutation<any, {
      weeklyNotificationEnabled: boolean;
      weeklyNotificationTitle: string;
      weeklyNotificationBody: string;
    }>({
      query: (body) => ({
        url: '/notifications/settings',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Notification'],
    }),

    searchUsers: builder.query<any, string>({
      query: (searchQuery) => ({
        url: '/users/search',
        params: { q: searchQuery },
      }),
    }),

    // アプリバージョン管理関連
    getAppVersion: builder.query<any, void>({
      query: () => '/settings/app-version',
      providesTags: ['Settings'],
    }),

    updateAppVersion: builder.mutation<any, {
      android: { version: string; buildNumber: number; minVersion: string; minBuildNumber: number };
      ios: { version: string; buildNumber: number; minVersion: string; minBuildNumber: number };
      forceUpdate: boolean;
      updateMessage: string;
    }>({
      query: (body) => ({
        url: '/settings/app-version',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Settings'],
    }),

    // 出金申請管理関連
    getPayoutRequests: builder.query<any, { status?: string; page?: number; limit?: number; showExcluded?: string }>({
      query: (params) => ({
        url: '/payout-requests',
        params,
      }),
      providesTags: ['PayoutRequest'],
    }),

    getPayoutRequestStats: builder.query<{
      pending: number;
      pendingAmount: number;
      processed: number;
      processedAmount: number;
      rejected: number;
      rejectedAmount: number;
      excludedCount: number;
      total: number;
    }, { showExcluded?: string } | void>({
      query: (params) => ({
        url: '/payout-requests/stats',
        params: params || {},
      }),
      providesTags: ['PayoutRequest'],
    }),

    approvePayoutRequest: builder.mutation<any, { id: string; notes?: string }>({
      query: ({ id, ...body }) => ({
        url: `/payout-requests/${id}/approve`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PayoutRequest'],
    }),

    rejectPayoutRequest: builder.mutation<any, { id: string; reason: string }>({
      query: ({ id, ...body }) => ({
        url: `/payout-requests/${id}/reject`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PayoutRequest'],
    }),

    // お知らせ管理関連
    getAnnouncements: builder.query<any, void>({
      query: () => '/announcements',
      providesTags: ['Announcement'],
    }),

    createAnnouncement: builder.mutation<any, { title: string; body: string; targetType: 'all' | 'verified' | 'individual'; targetUserIds?: string[] }>({
      query: (body) => ({
        url: '/announcements',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Announcement'],
    }),

    sendAnnouncementBatch: builder.mutation<
      { processed: number; total: number; batchSent: number; batchFailed: number; isComplete: boolean },
      { id: string; offset: number; limit: number }
    >({
      query: ({ id, offset, limit }) => ({
        url: `/announcements/${id}/send-batch`,
        method: 'POST',
        body: { offset, limit },
      }),
    }),

    searchAnnouncementUsers: builder.query<{ users: { id: string; username: string; email: string }[] }, string>({
      query: (q) => ({
        url: '/announcements/search-users',
        params: { q },
      }),
    }),

    // 本人確認書類の署名付きURL取得
    getDocumentSignedUrls: builder.mutation<{ urls: Record<string, string | null> }, { documentKeys: string[] }>({
      query: (body) => ({
        url: '/documents/signed-url',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const exportUsersCsv = async (type: 'all' | 'seller' | 'buyer', token: string) => {
  const baseUrl = getApiBaseUrl();
  const response = await fetch(`${baseUrl}/users/export-csv?type=${type}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`CSV export failed: ${response.status}`);
  }

  const blob = await response.blob();
  const typeLabel = type === 'seller' ? '出品者' : type === 'buyer' ? '購入者' : '全ユーザー';
  const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const filename = `${typeLabel}_${dateStr}.csv`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const {
  // 認証関連
  useLoginMutation,
  useVerifyTokenQuery,
  
  // ダッシュボード関連
  useGetDashboardStatsQuery,
  
  // ユーザー管理関連
  useGetUsersQuery,
  useGetUserQuery,
  useVerifyUserMutation,
  useBanUserMutation,
  useBlockUserPurchaseMutation,
  useDeleteUserMutation,
  useRestoreUserMutation,
  
  // 商品管理関連
  useGetProductsQuery,
  useGetProductQuery,
  useDeleteProductMutation,
  useHideProductMutation,
  useReviewProductMutation,
  useGetProductCommentsQuery,
  useGetProductCommentThreadsQuery,
  useGetFeaturedProductsQuery,
  useUpdateFeaturedProductsMutation,
  
  // 通報管理関連
  useGetReportsQuery,
  useGetReportQuery,
  useResolveReportMutation,
  
  // 問い合わせ管理関連
  useGetInquiriesQuery,
  useGetInquiryQuery,
  useRespondToInquiryMutation,
  
  // 取引管理関連
  useGetTransactionsQuery,
  useGetTransactionQuery,
  useConfirmPaymentMutation,
  useExcludeFromStatsMutation,
  
  // チャット管理関連
  useGetChatsQuery,
  useGetChatQuery,
  useGetChatMessagesQuery,
  useSendAdminContactMutation,

  // 銀行振り込み管理関連
  useGetBankTransfersQuery,
  useGetBankTransferStatsQuery,
  useGetOtherPaymentStatsQuery,
  useConfirmBankTransferMutation,

  // 設定管理関連
  useGetSystemSettingsQuery,
  useUpdateSystemSettingsMutation,
  useGetAdminSettingsQuery,
  useUpdateAdminSettingsMutation,
  useGetAdministratorsQuery,
  useCreateAdministratorMutation,
  useUpdateAdministratorMutation,
  useDeleteAdministratorMutation,

  // プッシュ通知管理関連
  useGetScheduledNotificationsQuery,
  useSendNotificationMutation,
  useCancelNotificationMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useSearchUsersQuery,

  // アプリバージョン管理関連
  useGetAppVersionQuery,
  useUpdateAppVersionMutation,

  // 出金申請管理関連
  useGetPayoutRequestsQuery,
  useGetPayoutRequestStatsQuery,
  useApprovePayoutRequestMutation,
  useRejectPayoutRequestMutation,

  // お知らせ管理関連
  useGetAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useSendAnnouncementBatchMutation,
  useSearchAnnouncementUsersQuery,

  // 本人確認書類の署名付きURL
  useGetDocumentSignedUrlsMutation,
} = adminApi; 